// planoGeo.js — geometría pura para el trazado sobre plano (calibración,
// área de una estancia dibujada, longitud de un tramo dibujado). Sin
// canvas ni React, para poder verificarlo con casos conocidos antes de
// conectarlo al editor — mismo enfoque que ductCalc.js.

export function distanciaPx(p1, p2) {
  return Math.hypot(p2.x - p1.x, p2.y - p1.y);
}

// Fórmula del shoelace — área de un polígono simple a partir de sus
// vértices en píxeles, convertida a m² con la escala calibrada.
export function areaPoligonoM2(puntosPx, mmPorPx) {
  if (puntosPx.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < puntosPx.length; i++) {
    const a = puntosPx[i];
    const b = puntosPx[(i + 1) % puntosPx.length];
    sum += a.x * b.y - b.x * a.y;
  }
  const areaPx2 = Math.abs(sum) / 2;
  const areaMm2 = areaPx2 * mmPorPx * mmPorPx;
  return areaMm2 / 1e6; // mm² -> m²
}

// Longitud total de una polilínea (suma de segmentos), convertida a metros.
export function longitudTrazadoM(puntosPx, mmPorPx) {
  if (puntosPx.length < 2) return 0;
  let sumPx = 0;
  for (let i = 0; i < puntosPx.length - 1; i++) sumPx += distanciaPx(puntosPx[i], puntosPx[i + 1]);
  return (sumPx * mmPorPx) / 1000; // mm -> m
}

export function calcularMmPorPx(p1, p2, distanciaRealM) {
  const px = distanciaPx(p1, p2);
  if (px === 0) return 0;
  return (distanciaRealM * 1000) / px;
}

// Punto más cercano a "p" sobre cualquier segmento de una polilínea (no solo
// sus vértices) — permite enganchar una derivación en mitad del trazado de
// otro tramo, no solo en sus extremos. Vive aquí (no en PlanoTracer.js ni en
// ductCalc.js) para que el punto donde el canvas engancha el clic y el punto
// donde el motor de cálculo detecta la conexión sean exactamente el mismo
// cálculo — si vivieran por separado podrían no coincidir.
export function puntoMasCercanoEnPolilinea(p, puntos) {
  let mejor = null, mejorDist = Infinity;
  for (let i = 0; i < puntos.length - 1; i++) {
    const a = puntos[i], b = puntos[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 === 0 ? 0 : ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const candidato = { x: a.x + t * dx, y: a.y + t * dy };
    const d = distanciaPx(p, candidato);
    if (d < mejorDist) { mejorDist = d; mejor = candidato; }
  }
  return mejor ? { punto: mejor, distancia: mejorDist } : null;
}

// Cuenta los giros reales de un trazado (vértices internos donde el ángulo
// entrante/saliente se aparta de 0°/180°) — como el editor ya fuerza los
// giros a 90° exactos al trazar (snapOrtogonal en PlanoTracer.js), no hace
// falta más margen que separar "recto" de "gira" con holgura de 30°.
export function contarCodos(puntosPx, toleranciaGrados = 30) {
  if (!puntosPx || puntosPx.length < 3) return 0;
  let codos = 0;
  for (let i = 1; i < puntosPx.length - 1; i++) {
    const a = puntosPx[i - 1], b = puntosPx[i], c = puntosPx[i + 1];
    const ang1 = Math.atan2(b.y - a.y, b.x - a.x);
    const ang2 = Math.atan2(c.y - b.y, c.x - b.x);
    let diff = Math.abs(((ang2 - ang1) * 180) / Math.PI);
    if (diff > 180) diff = 360 - diff;
    if (diff > toleranciaGrados) codos++;
  }
  return codos;
}
